// import { memo } from "react";
// import PropTypes from "prop-types";
// import { useRouter } from "next/router";
// import Link from "next/link";

// /* ---------------------------------------------
//    Shared Pagination (rounded pill style)
//    ---------------------------------------------
//    Props:
//    - meta: { page, totalPages }  (required)
//    - locale: "en" | "ar"
//    - hrefForPage: optional (page) => Link href object/string.
//        Defaults to preserving all current query params and only
//        swapping `page`. Pass this in if a page needs custom query
//        building (e.g. removing/keeping a category filter).
//    - ariaLabel: optional string for the <nav> aria-label
// ---------------------------------------------- */
// function Pagination({
//   meta,
//   locale = "en",
//   hrefForPage,
//   ariaLabel = "Pagination",
// }) {
//   const router = useRouter();

//   if (!meta || !meta.totalPages || meta.totalPages <= 1) return null;

//   const { page, totalPages } = meta;

//   const defaultHrefForPage = (p) => ({
//     pathname: router.pathname,
//     query: { ...router.query, page: p },
//   });

//   const buildHref = hrefForPage || defaultHrefForPage;

//   // Windowed page numbers: first, last, and up to 2 pages either side of current
//   const pageNumbers = Array.from(
//     { length: totalPages },
//     (_, i) => i + 1,
//   ).filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2);

//   const withEllipses = [];
//   let prevP = 0;
//   for (const p of pageNumbers) {
//     if (prevP && p - prevP > 1) withEllipses.push("ellipsis-" + p);
//     withEllipses.push(p);
//     prevP = p;
//   }

//   return (
//     <nav
//       aria-label={ariaLabel}
//       className="mt-10 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2"
//     >
//       <Link
//         href={buildHref(Math.max(1, page - 1))}
//         aria-disabled={page <= 1}
//         className={`px-2 text-sm font-semibold no-underline transition-colors ${
//           page <= 1
//             ? "pointer-events-none text-neutral-300"
//             : "text-neutral-950 hover:text-neutral-600"
//         }`}
//       >
//         {locale === "ar" ? "السابق" : "Prev"}
//       </Link>

//       {withEllipses.map((p) =>
//         typeof p === "string" ? (
//           <span
//             key={p}
//             className="flex h-9 min-w-9 items-center justify-center text-sm font-semibold text-neutral-400"
//           >
//             …
//           </span>
//         ) : (
//           <Link
//             key={p}
//             href={buildHref(p)}
//             className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold no-underline transition-colors ${
//               p === page
//                 ? "bg-neutral-950 text-white"
//                 : "text-neutral-950 hover:bg-neutral-100"
//             }`}
//           >
//             {p}
//           </Link>
//         ),
//       )}

//       <Link
//         href={buildHref(Math.min(totalPages, page + 1))}
//         aria-disabled={page >= totalPages}
//         className={`px-2 text-sm font-semibold no-underline transition-colors ${
//           page >= totalPages
//             ? "pointer-events-none text-neutral-300"
//             : "text-neutral-950 hover:text-neutral-600"
//         }`}
//       >
//         {locale === "ar" ? "التالي" : "Next"}
//       </Link>
//     </nav>
//   );
// }

// Pagination.propTypes = {
//   meta: PropTypes.shape({
//     page: PropTypes.number,
//     totalPages: PropTypes.number,
//   }),
//   locale: PropTypes.string,
//   hrefForPage: PropTypes.func,
//   ariaLabel: PropTypes.string,
// };

// export default memo(Pagination);

//components/utils/pagination.jsx
import { memo } from "react";
import PropTypes from "prop-types";
import { useRouter } from "next/router";
import Link from "next/link";

/* ---------------------------------------------
   Shared Pagination (rounded pill style)
   ---------------------------------------------
   Props:
   - meta: { page, totalPages }  (required)
   - locale: "en" | "ar"
   - hrefForPage: optional (page) => Link href object/string.
       Defaults to keeping the current query params (other than
       `page`) and adding `page` only when it is 2 or higher, so
       page 1 always links to the clean URL (no ?page=1).
   - ariaLabel: optional string for the <nav> aria-label
---------------------------------------------- */
function Pagination({
  meta,
  locale = "en",
  hrefForPage,
  ariaLabel = "Pagination",
}) {
  const router = useRouter();

  if (!meta || !meta.totalPages || meta.totalPages <= 1) return null;

  const { page, totalPages } = meta;

  const defaultHrefForPage = (p) => {
    const { page: _removed, ...rest } = router.query;
    const query = { ...rest };
    if (p > 1) query.page = p;
    return { pathname: router.pathname, query };
  };

  const buildHref = hrefForPage || defaultHrefForPage;

  // Windowed page numbers: first, last, and up to 2 pages either side of current
  const pageNumbers = Array.from(
    { length: totalPages },
    (_, i) => i + 1,
  ).filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2);

  const withEllipses = [];
  let prevP = 0;
  for (const p of pageNumbers) {
    if (prevP && p - prevP > 1) withEllipses.push("ellipsis-" + p);
    withEllipses.push(p);
    prevP = p;
  }

  const prevLabel = locale === "ar" ? "السابق" : "Prev";
  const nextLabel = locale === "ar" ? "التالي" : "Next";

  const edgeBase = "px-2 text-sm font-semibold no-underline transition-colors";
  const edgeActive = "text-neutral-950 hover:text-neutral-600";
  const edgeDisabled = "text-neutral-300";

  return (
    <nav
      aria-label={ariaLabel}
      className="mt-10 flex flex-wrap items-center justify-center gap-1.5 sm:gap-2"
    >
      {/* Disabled Prev/Next are plain text, not self-linking anchors */}
      {page <= 1 ? (
        <span aria-disabled="true" className={`${edgeBase} ${edgeDisabled}`}>
          {prevLabel}
        </span>
      ) : (
        <Link
          href={buildHref(page - 1)}
          rel="prev"
          className={`${edgeBase} ${edgeActive}`}
        >
          {prevLabel}
        </Link>
      )}

      {withEllipses.map((p) =>
        typeof p === "string" ? (
          <span
            key={p}
            className="flex h-9 min-w-9 items-center justify-center text-sm font-semibold text-neutral-400"
          >
            …
          </span>
        ) : (
          <Link
            key={p}
            href={buildHref(p)}
            aria-current={p === page ? "page" : undefined}
            className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold no-underline transition-colors ${
              p === page
                ? "bg-neutral-950 text-white"
                : "text-neutral-950 hover:bg-neutral-100"
            }`}
          >
            {p}
          </Link>
        ),
      )}

      {page >= totalPages ? (
        <span aria-disabled="true" className={`${edgeBase} ${edgeDisabled}`}>
          {nextLabel}
        </span>
      ) : (
        <Link
          href={buildHref(page + 1)}
          rel="next"
          className={`${edgeBase} ${edgeActive}`}
        >
          {nextLabel}
        </Link>
      )}
    </nav>
  );
}

Pagination.propTypes = {
  meta: PropTypes.shape({
    page: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  locale: PropTypes.string,
  hrefForPage: PropTypes.func,
  ariaLabel: PropTypes.string,
};

export default memo(Pagination);
